import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PageTurn, Paginate } from '../components/Paginate';

const PAGE_TABLE = 10;

const STATUS_META = {
  pending:  { label: 'Pending',  color: '#ffd700', bg: 'rgba(255,215,0,0.08)',   border: 'rgba(255,215,0,0.25)' },
  invited:  { label: 'Invited',  color: '#00d4ff', bg: 'rgba(0,212,255,0.08)',   border: 'rgba(0,212,255,0.25)' },
  approved: { label: 'Approved', color: '#00ff88', bg: 'rgba(0,255,136,0.08)',   border: 'rgba(0,255,136,0.25)' },
  declined: { label: 'Declined', color: '#ff4444', bg: 'rgba(255,68,68,0.08)',   border: 'rgba(255,68,68,0.25)' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 3, background: m.bg, border: `1px solid ${m.border}`, color: m.color, fontSize: 10, fontFamily: 'Share Tech Mono', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
      {m.label}
    </span>
  );
}

export default function Waitlist() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [toast, setToast] = useState(null);
  const [acting, setActing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [mailAuth, setMailAuth] = useState(null); // { authorized, account }

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4500);
  };

  const load = () => {
    setLoading(true);
    api.get('/waitlist')
      .then(r => setList(r.data))
      .catch(() => showToast('Failed to load waitlist', false))
      .finally(() => setLoading(false));
  };

  const loadMailAuth = () => {
    api.get('/admin/mail-auth/status', { silent: true })
      .then(r => setMailAuth(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); loadMailAuth(); }, []); // eslint-disable-line

  const setStatus = async (entry, newStatus) => {
    setActing(entry.id);
    try {
      const { data } = await api.patch(`/waitlist/${entry.id}`, { status: newStatus });
      setList(prev => prev.map(w => w.id === data.id ? data : w));
      if (newStatus === 'approved') {
        showToast(`✅ Account created for ${entry.name}! Welcome email sent to ${entry.email}.`);
      } else if (newStatus === 'declined') {
        showToast(`🚫 ${entry.name}'s application has been declined.`);
      } else if (newStatus === 'invited') {
        showToast(`📨 ${entry.name} marked as invited.`);
      } else {
        showToast(`Status updated to ${newStatus}.`);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Action failed', false);
    }
    setActing(null);
    setConfirmModal(null);
  };

  const fastTrack = async (entry) => {
    setActing(entry.id);
    try {
      const { data } = await api.post(`/waitlist/${entry.id}/fast-track`);
      setList(prev => prev.map(w => w.id === data.entry.id ? data.entry : w));
      showToast(`⚡ Fast-tracked! Account created for ${entry.name}, welcome email sent.`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Fast-track failed', false);
    }
    setActing(null);
  };

  const remove = async (entry) => {
    setActing(entry.id);
    try {
      await api.delete(`/waitlist/${entry.id}`);
      setList(prev => prev.filter(w => w.id !== entry.id));
      showToast(`Removed ${entry.name} from waitlist.`);
    } catch {
      showToast('Delete failed', false);
    }
    setActing(null);
    setConfirmModal(null);
  };

  const counts = {
    all: list.length,
    pending: list.filter(w => w.status === 'pending').length,
    invited: list.filter(w => w.status === 'invited').length,
    approved: list.filter(w => w.status === 'approved').length,
    declined: list.filter(w => w.status === 'declined').length,
  };

  const filtered = filter === 'all' ? list : list.filter(w => w.status === filter);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' }) : '—';

  if (!can('cwg', 'stakeholder')) {
    return <div className="empty-state">Access restricted to CWG / Stakeholder.</div>;
  }

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, padding: '10px 20px', borderRadius: 6, background: toast.ok ? 'rgba(0,20,10,0.97)' : 'rgba(40,0,0,0.97)', border: `1px solid ${toast.ok ? '#00ff88' : '#ff4444'}`, color: toast.ok ? '#00ff88' : '#ff7777', fontSize: 13, fontFamily: 'Share Tech Mono', whiteSpace: 'nowrap', boxShadow: `0 0 24px ${toast.ok ? 'rgba(0,255,136,0.2)' : 'rgba(255,68,68,0.2)'}` }}>
          {toast.msg}
        </div>
      )}

      {/* Confirm modal */}
      {confirmModal && (
        <div className="modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title" style={{ color: confirmModal.action === 'approved' ? '#00ff88' : confirmModal.action === 'declined' ? '#ff4444' : 'var(--j1)' }}>
              {confirmModal.action === 'approved' ? 'APPROVE & CREATE ACCOUNT' : confirmModal.action === 'declined' ? 'DECLINE APPLICATION' : 'CONFIRM ACTION'}
            </div>
            <div style={{ fontSize: 14, color: 'var(--txt2)', lineHeight: 1.6, margin: '14px 0 22px' }}>
              {confirmModal.action === 'approved' ? (
                <>
                  This will <strong style={{ color: '#00ff88' }}>create a login account</strong> for <strong style={{ color: 'var(--txt)' }}>{confirmModal.entry.name}</strong> and send a welcome email to <strong style={{ color: '#00d4ff' }}>{confirmModal.entry.email}</strong> with their temporary credentials.<br /><br />
                  <span style={{ fontSize: 12, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Assigned role: {confirmModal.entry.role}</span>
                </>
              ) : confirmModal.action === 'declined' ? (
                <>Are you sure you want to decline <strong style={{ color: 'var(--txt)' }}>{confirmModal.entry.name}</strong>'s application? This action can be reversed later.</>
              ) : (
                <>Confirm action on {confirmModal.entry.name}.</>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1 }} onClick={() => setConfirmModal(null)}>CANCEL</button>
              <button
                className={`btn ${confirmModal.action === 'approved' ? 'btn-primary' : 'btn-danger'}`}
                style={{ flex: 2 }}
                disabled={acting === confirmModal.entry.id}
                onClick={() => confirmModal.action === 'delete' ? remove(confirmModal.entry) : setStatus(confirmModal.entry, confirmModal.action)}
              >
                {acting === confirmModal.entry.id ? 'PROCESSING…' : confirmModal.action === 'approved' ? '✅ APPROVE & SEND EMAIL' : confirmModal.action === 'declined' ? '🚫 DECLINE' : 'DELETE'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div className="page-title">WAITLIST ADMIN</div>
          <div className="page-sub">Review and approve member applications — approval creates accounts and sends welcome emails</div>
        </div>

        {/* Mail sender authorization panel */}
        {can('cwg') && (
          <div className="card" style={{ padding: '14px 18px', minWidth: 280, flexShrink: 0, border: `1px solid ${mailAuth?.authorized ? 'rgba(0,255,136,0.3)' : 'rgba(255,215,0,0.3)'}` }}>
            <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 8 }}>MAIL SENDER</div>
            {mailAuth?.authorized ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00ff88', display: 'inline-block' }} />
                  <span style={{ fontSize: 12, color: '#00ff88', fontFamily: 'Share Tech Mono', fontWeight: 700 }}>AUTHORIZED</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--txt2)', marginBottom: 10, fontFamily: 'Share Tech Mono' }}>{mailAuth.account}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a href="/api/admin/mail-auth/authorize" target="_blank" rel="noreferrer"
                    style={{ fontSize: 10, padding: '5px 10px', background: 'transparent', border: '1px solid var(--brd)', borderRadius: 3, color: 'var(--txt2)', textDecoration: 'none', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
                    ↻ RE-AUTH
                  </a>
                  <button onClick={() => api.delete('/admin/mail-auth/revoke').then(() => { setMailAuth({ authorized: false }); showToast('Mail auth revoked'); })}
                    style={{ fontSize: 10, padding: '5px 10px', background: 'transparent', border: '1px solid rgba(255,68,68,0.3)', borderRadius: 3, color: '#ff7777', cursor: 'pointer', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
                    REVOKE
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ffd700', display: 'inline-block' }} />
                  <span style={{ fontSize: 12, color: '#ffd700', fontFamily: 'Share Tech Mono', fontWeight: 700 }}>NOT AUTHORIZED</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--txt2)', marginBottom: 10, lineHeight: 1.5 }}>
                  Authorize your Wissen account to send welcome emails via Microsoft Graph.
                </div>
                <a href="/api/admin/mail-auth/authorize" target="_blank" rel="noreferrer"
                  onClick={() => setTimeout(loadMailAuth, 5000)}
                  style={{ display: 'inline-block', padding: '8px 16px', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.4)', borderRadius: 4, color: '#00d4ff', textDecoration: 'none', fontFamily: 'Share Tech Mono', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
                  🔑 AUTHORIZE MAIL SENDER
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { key: 'all',      label: 'Total',    color: 'var(--txt)',  icon: '📋' },
          { key: 'pending',  label: 'Pending',  color: '#ffd700',    icon: '⏳' },
          { key: 'invited',  label: 'Invited',  color: '#00d4ff',    icon: '📨' },
          { key: 'approved', label: 'Approved', color: '#00ff88',    icon: '✅' },
          { key: 'declined', label: 'Declined', color: '#ff4444',    icon: '🚫' },
        ].map(s => (
          <div
            key={s.key}
            onClick={() => { setFilter(s.key); setPage(0); }}
            className="card"
            style={{ padding: '14px 16px', cursor: 'pointer', border: `1px solid ${filter === s.key ? s.color : 'var(--brd2)'}`, background: filter === s.key ? `rgba(0,0,0,0.4)` : 'var(--bg3)', transition: 'all 0.15s' }}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontFamily: 'Orbitron', fontSize: 22, fontWeight: 800, color: s.color }}>{counts[s.key]}</div>
            <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <PageTurn page={page}>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--brd2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontFamily: 'Share Tech Mono', fontSize: 11, color: 'var(--txt2)', letterSpacing: 1 }}>
            {filtered.length} {filter === 'all' ? 'total' : filter} application{filtered.length !== 1 ? 's' : ''}
          </div>
          <button className="btn btn-sm" onClick={load} style={{ fontSize: 10 }}>↻ REFRESH</button>
        </div>

        {loading ? (
          <div className="empty-state">Loading waitlist…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">No {filter === 'all' ? '' : filter} applications</div>
        ) : (
          <table className="table" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: '22%' }}>APPLICANT</th>
                <th style={{ width: '22%' }}>EMAIL</th>
                <th style={{ width: '16%' }}>INTEREST</th>
                <th style={{ width: '10%' }}>STATUS</th>
                <th style={{ width: '10%' }}>APPLIED</th>
                <th style={{ width: '20%' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(page * PAGE_TABLE, (page + 1) * PAGE_TABLE).map(entry => {
                const isExpanded = expandedId === entry.id;
                return (
                  <React.Fragment key={entry.id}>
                    <tr
                      style={{ cursor: 'pointer', background: isExpanded ? 'rgba(0,212,255,0.03)' : 'transparent' }}
                      onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,212,255,0.1)', border: '1px solid var(--brd)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--j1)', flexShrink: 0 }}>
                            {entry.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--txt)', fontSize: 13 }}>{entry.name}</div>
                            {entry.userId && <div style={{ fontSize: 9, color: '#00ff88', fontFamily: 'Share Tech Mono' }}>ACCOUNT CREATED</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.email}</td>
                      <td style={{ fontSize: 11, color: 'var(--txt2)' }}>{entry.role}</td>
                      <td><StatusBadge status={entry.status} /></td>
                      <td style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{fmtDate(entry.createdAt)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {!entry.userId && can('cwg') && (
                            <button
                              className="btn btn-sm"
                              style={{ fontSize: 10, padding: '4px 10px', borderColor: 'rgba(255,215,0,0.5)', color: '#ffd700', fontWeight: 700 }}
                              disabled={!!acting}
                              title="Fast-track: skip queue and create account immediately"
                              onClick={() => fastTrack(entry)}
                            >
                              ⚡ JUMP
                            </button>
                          )}
                          {entry.status !== 'approved' && can('cwg') && (
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ fontSize: 10, padding: '4px 10px' }}
                              disabled={!!acting}
                              onClick={() => setConfirmModal({ entry, action: 'approved' })}
                            >
                              ✅ APPROVE
                            </button>
                          )}
                          {entry.status === 'pending' && (
                            <button
                              className="btn btn-sm"
                              style={{ fontSize: 10, padding: '4px 10px', borderColor: '#00d4ff', color: '#00d4ff' }}
                              disabled={!!acting}
                              onClick={() => setStatus(entry, 'invited')}
                            >
                              📨 INVITE
                            </button>
                          )}
                          {entry.status !== 'declined' && entry.status !== 'approved' && (
                            <button
                              className="btn btn-danger btn-sm"
                              style={{ fontSize: 10, padding: '4px 10px' }}
                              disabled={!!acting}
                              onClick={() => setConfirmModal({ entry, action: 'declined' })}
                            >
                              🚫
                            </button>
                          )}
                          {can('cwg') && (
                            <button
                              className="btn btn-sm"
                              style={{ fontSize: 10, padding: '4px 8px', borderColor: 'var(--brd2)', color: 'var(--txt3)' }}
                              disabled={!!acting}
                              onClick={() => setConfirmModal({ entry, action: 'delete' })}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} style={{ background: 'rgba(0,212,255,0.02)', padding: '14px 20px', borderBottom: '1px solid var(--brd2)' }}>
                          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 200px' }}>
                              <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 6 }}>APPLICATION NOTE</div>
                              <div style={{ fontSize: 13, color: 'var(--txt2)', lineHeight: 1.6 }}>{entry.note || <span style={{ color: 'var(--txt3)', fontStyle: 'italic' }}>No note provided</span>}</div>
                            </div>
                            <div style={{ flex: '0 0 200px' }}>
                              <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 6 }}>DETAILS</div>
                              <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.8, fontFamily: 'Share Tech Mono' }}>
                                <div>Applied: {fmtDate(entry.createdAt)}</div>
                                {entry.approvedAt && <div style={{ color: '#00ff88' }}>Approved: {fmtDate(entry.approvedAt)}</div>}
                                {entry.userId && <div style={{ color: '#00ff88' }}>User ID: {entry.userId.slice(0, 8)}…</div>}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </PageTurn>
      <Paginate page={page} total={filtered.length} pageSize={PAGE_TABLE} onPage={setPage} />
    </div>
  );
}
